import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface SettingsColumnHead {
	id: string;
	header: ReactNode;
	/** Width and alignment, e.g. `w-[150px] justify-center`; omit to take the leftover width. */
	className?: string;
}

export interface SettingsColumn<T> extends SettingsColumnHead {
	cell: (row: T) => ReactNode;
}

const cellClass = (column: SettingsColumnHead, index: number) =>
	cn('flex min-w-0 items-center px-3', index === 0 && 'pl-4', column.className ?? 'flex-1');

/** Column titles; put it in the page's sticky `subHeader` so it stays in view while scrolling. */
export function SettingsTableHead({ columns }: { columns: SettingsColumnHead[] }) {
	return (
		<div role='row' className='flex w-full items-stretch border-b border-gray-500 bg-gray-50'>
			{columns.map((column, i) => (
				<div
					key={column.id}
					role='columnheader'
					className={cn(cellClass(column, i), 'min-h-9 gap-1 py-1 text-[13px] font-medium leading-[18px] whitespace-nowrap text-gray-800', i > 0 && 'border-l border-gray-300')}
				>
					{column.header}
				</div>
			))}
		</div>
	);
}

interface SettingsTableRowsProps<T> {
	columns: SettingsColumn<T>[];
	rows: T[];
	rowKey: (row: T) => string;
	isLoading?: boolean;
	/** Shown when there are no rows. */
	empty: ReactNode;
	onRowClick?: (row: T) => void;
	rowClassName?: (row: T) => string | undefined;
}

const CELL_DIVIDER_CLASS = 'before:absolute before:left-0 before:top-1/2 before:h-4 before:w-px before:-translate-y-1/2 before:bg-gray-300';

/** Rows with the short vertical dividers between cells (Figma settings tables). */
export function SettingsTableRows<T>({ columns, rows, rowKey, isLoading, empty, onRowClick, rowClassName }: SettingsTableRowsProps<T>) {
	// Same columns and dividers as the real rows, so the table keeps its shape while loading.
	if (isLoading) {
		return (
			<div className='flex flex-col'>
				{Array.from({ length: 6 }).map((_, row) => (
					<div key={row} className='flex min-h-12 w-full items-stretch border-b border-gray-300 bg-white'>
						{columns.map((column, i) => (
							<div key={column.id} className={cn(cellClass(column, i), 'relative py-3', i > 0 && CELL_DIVIDER_CLASS)}>
								{column.header !== null && <Skeleton className='h-3.5 w-3/4' />}
							</div>
						))}
					</div>
				))}
			</div>
		);
	}
	if (rows.length === 0) return <div className='px-4 py-12 text-center text-[13px] text-gray-800'>{empty}</div>;

	return (
		<div role='rowgroup' className='flex flex-col'>
			{rows.map((row) => (
				<div
					key={rowKey(row)}
					role='row'
					tabIndex={onRowClick ? 0 : undefined}
					onClick={onRowClick ? () => onRowClick(row) : undefined}
					onKeyDown={(e) => {
						// A key on a link or button inside the row is that control's, not the row's.
						if (!onRowClick || e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
						e.preventDefault();
						onRowClick(row);
					}}
					className={cn(
						'flex min-h-12 w-full items-stretch border-b border-gray-300 bg-white text-[13px] leading-[18px] text-ink',
						onRowClick && 'cursor-pointer outline-none hover:bg-gray-50 focus-visible:bg-gray-50',
						rowClassName?.(row)
					)}
				>
					{columns.map((column, i) => (
						<div
							key={column.id}
							role='cell'
							className={cn(
								cellClass(column, i),
								'relative py-3',
								i > 0 && CELL_DIVIDER_CLASS
							)}
						>
							{column.cell(row)}
						</div>
					))}
				</div>
			))}
		</div>
	);
}
